import type { LanguageDto, LanguageIdDto, SelfDto, TranslationRequest, TranslationsDto } from "@/api/types";
import type { Languages } from "@/translations";

export type FormTranslations = Record<LanguageDto["id"], string>;
export const FALLBACK_UI_LANGUAGE: Languages = "en";

const UI_LANGUAGE_IDS = {
  nl: true,
  en: true,
  de: true,
  pt: true,
  es: true,
  pl: true,
  fr: true,
  it: true,
  tr: true,
  bg: true,
  hu: true,
  uk: true,
  ro: true,
  dutch: true,
  english: true,
  german: true,
} satisfies Record<LanguageIdDto, true>;

/** Narrows a language ID to the languages supported by the static UI catalogue. */
export function isUiLanguageId(languageId: string): languageId is LanguageIdDto {
  return Object.hasOwn(UI_LANGUAGE_IDS, languageId);
}

/** Returns a static UI language ID and throws when the language is auto-translation-only. */
export function toUiLanguageId(languageId: LanguageDto["id"]): LanguageIdDto {
  if (!isUiLanguageId(languageId)) {
    throw new Error(`Language ${languageId} cannot be used for UI content translations`);
  }

  return languageId;
}

export function createFormTranslations<
  TTranslation extends (TTranslationKey extends never ? TranslationsDto : { languageId: LanguageDto["id"] }),
  TTranslationKey extends keyof TTranslation,
>(data: {
  languages: LanguageDto[];
  translations?: TTranslation[] | undefined;
  translationKey?: TTranslationKey;
}): FormTranslations {
  const textSelector = data.translationKey;

  return Object.fromEntries(
    data.languages.map((lng) => {
      const translation = data.translations?.find((translation) => lng.id === translation.languageId);
      const value = translation && (textSelector ? translation[textSelector] : (translation as TranslationsDto).value);

      return [lng.id, value || ""];
    }),
  ) as FormTranslations;
}

// Reads the value in the content's original language, used to edit the source text of
// auto-translated content in single-language forms.
export function getOriginalTranslationValue(
  translations: TranslationsDto[] | undefined,
  originalLanguageId: LanguageDto["id"],
): string {
  return translations?.find((translation) => translation.languageId === originalLanguageId)?.value ?? "";
}

export function toTranslationsRequest(formTranslations: FormTranslations): TranslationRequest[] {
  return Object.entries(formTranslations)
    .filter(([, value]) => value.trim() !== "")
    .map<TranslationRequest>(([languageId, value]) => ({
      languageId: toUiLanguageId(languageId),
      value,
    }));
}

// The content is auto-translated if the session user has a different auto-translation language than the original language.
export const isContentAutoTranslated = (originalLanguageId: LanguageDto["id"], sessionUser: SelfDto): boolean => {
  return !!sessionUser.autoTranslationLanguage?.id && sessionUser.autoTranslationLanguage.id !== originalLanguageId;
};
